import * as React from 'react';

import { Route, RouteComponentProps, Switch, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { HelpPanel } from '@amzn/et-polaris-utils';
import ReactMarkdown from 'react-markdown/with-html';
import { ReactElement, useLayoutEffect } from 'react';
import { Location } from 'history';
import I18n from '../setupI18n';

export interface HelpContentLinks {
  url: string;
  label: string;
}

export interface HelpContentSpec {
  title: string;
  content: string;
  roleContent?: {
    ADMIN?: string;
    PM?: string;
    LINGUIST?: string;
  };
  internalLinks?: HelpContentLinks[];
  externalLinks?: HelpContentLinks[];
  paths?: string[];
}

export interface HelpContentCollectionSpec {
  [key: string]: HelpContentSpec;
}

export interface HelpContentProps {
  location: Location;
  defaultHelpKey: string;
  helpResolver(key: string): HelpContentSpec;
  setHelpOpen(value: boolean): void;
  setHasHelpContent(value: boolean): void;
  userRole: 'ADMIN' | 'PM' | 'LINGUIST';
  isInternalUser: boolean;
}

export const HelpContent = ({
  location: { hash },
  defaultHelpKey,
  helpResolver,
  setHelpOpen,
  setHasHelpContent,
  userRole,
  isInternalUser,
}: HelpContentProps): ReactElement => {
  const key = hash.length > 0 ? hash.substr(1) : defaultHelpKey;
  const helpContent = helpResolver(key);

  // TODO: Any help key will work on any page
  // TODO: An invalid help key will result in a blank help pane
  useLayoutEffect(() => {
    setHelpOpen(hash.length > 0);
  }, [hash, setHelpOpen]);

  useLayoutEffect(() => {
    setHasHelpContent(helpContent !== undefined);
  }, [helpContent, setHasHelpContent]);

  return (
    (helpContent && (
      <HelpPanel
        title={helpContent.title}
        content={
          <ReactMarkdown source={helpContent?.roleContent?.[userRole] ?? helpContent.content} />
        }
        links={isInternalUser ? helpContent.internalLinks : helpContent.externalLinks}
      />
    )) ||
    null
  );
};

export interface HelpContentRouterProps {
  helpContent: HelpContentCollectionSpec;
  helpResolver(key: string): HelpContentSpec;
  setHelpOpen(value: boolean): void;
  setHasHelpContent(value: boolean): void;
  userRole: 'ADMIN' | 'PM' | 'LINGUIST';
  isInternalUser: boolean;
}

export const HelpContentRouter = ({
  helpContent,
  helpResolver,
  setHelpOpen,
  setHasHelpContent,
  userRole,
  isInternalUser,
}: HelpContentRouterProps): ReactElement => {
  return (
    <Switch>
      {Object.entries(helpContent).map(
        ([key, value]) =>
          value.paths && (
            <Route
              key={key}
              path={value['paths']}
              // eslint-disable-next-line react/no-children-prop
              children={({ match, ...props }): ReactElement => {
                setHasHelpContent(match != null);

                return (
                  <HelpContent
                    defaultHelpKey={key}
                    helpResolver={helpResolver}
                    setHelpOpen={setHelpOpen}
                    setHasHelpContent={setHasHelpContent}
                    userRole={userRole}
                    isInternalUser={isInternalUser}
                    {...props}
                  />
                );
              }}
            />
          )
      )}
    </Switch>
  );
};

export interface HelpInfoLinkProps extends RouteComponentProps {
  helpId: string;
  location: Location;
}

export const HelpInfoLink = withRouter(
  ({ helpId, location }: HelpInfoLinkProps): ReactElement => {
    return (
      <Link className="awsui-util-help-info-link" to={{ ...location, hash: helpId }}>
        {I18n.t('Info')}
      </Link>
    );
  }
);
